import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import Preloader from '@omilia/preloader';
import ErrorBoundary from 'react-error-boundary';

import { theme } from 'theme';
import { getIsLoggedIn } from 'store/services/session/selectors';
import store, { history } from 'store';
import ProtectedRoute from 'components/ProtectedRoute';
import GenericError from 'components/GenericError';
import ProjectWrapper from 'components/ProjectWrapper';

import '../../styles/main.scss';

import Bounder from 'scenes/Bounder';
import Flow from 'scenes/Flow';
import Login from 'scenes/Login';
import Rules from 'scenes/Rules';
import Xpacks from 'scenes/Xpacks';
import Intents from 'scenes/Intents';
import Contexts from 'scenes/Contexts';
import Projects from 'scenes/Projects';
import NoAccess from 'scenes/NoAccess';
import NotFound from 'scenes/NotFound';
import Entities from 'scenes/Entities';
import Resources from 'scenes/Resources';
import Dictionaries from 'scenes/Dictionaries';
import ResetPassword from 'scenes/ResetPassword';
import ForgotPassword from 'scenes/ForgotPassword';
import TestSetsEditor from 'scenes/TestSetsEditor';
import VerifyAccount from 'scenes/VerifyAccount';
import { KeycloakActionsProvider } from 'hooks/useKeycloak';

// Bootstrap Sentry only when NLUSTUDIO_SENTRY_DSN exists
if (process.env.NLUSTUDIO_SENTRY_DSN) {
  // eslint-disable-next-line global-require
  require('@sentry/browser').init({
    dsn: process.env.NLUSTUDIO_SENTRY_DSN,
    sendDefaultPii: true,
    environment: process.env.NLUSTUDIO_ENVIRONMENT,
    release: process.env.NLUSTUDIO_RELEASE,
  });
}

const lazyLoadingComponents = {
  NewFlow: React.lazy(() =>
    import(/* webpackChunkName: "NewFlow" */ '../NewFlow'),
  ),
  ApplicationSetup: React.lazy(() =>
    import(
      /* webpackChunkName: "ApplicationSetup" */ '../NewFlow/scenes/ApplicationSetup'
    ),
  ),
  ProjectSettings: React.lazy(() =>
    import(/* webpackChunkName: "ProjectSettings" */ '../ProjectSettings'),
  ),
  Profile: React.lazy(() =>
    import(/* webpackChunkName: "Profile" */ '../Profile'),
  ),
  ChatLogs: React.lazy(() =>
    import(/* webpackChunkName: "ChatLogs" */ '../ChatLogs'),
  ),
  ResolveConflicts: React.lazy(() => import('../ResolveConflicts')),
};

const PROJECT_PATH = '/:company/:project';

type Props = {
  authenticated: boolean;
  keycloak?: {
    login: () => void;
    logout: () => void;
    token: string;
  };
};

const Routes = ({ authenticated, keycloak }: Props) => {
  const { login, logout, token } = keycloak || {
    login: undefined,
    logout: undefined,
    token: undefined,
  };

  const [useKeycloak, setUseKeycloak] = React.useState(authenticated);

  return (
    <KeycloakActionsProvider
      value={{
        login,
        logout,
        usingKeycloak: useKeycloak,
        token,
        setKeycloakEnabled: () => setUseKeycloak(true),
      }}
    >
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <ErrorBoundary FallbackComponent={GenericError}>
              <React.Suspense fallback={<Preloader />}>
                <Switch>
                  {/* ERRORS */}
                  <Route exact component={NotFound} path="/not-found" />
                  <Route exact component={NoAccess} path="/no-access" />

                  {/* AUTH */}
                  <Route
                    exact
                    path="/sign_in"
                    render={() =>
                      getIsLoggedIn(store.getState()) === true ? (
                        <Redirect to="/" />
                      ) : (
                        <Login hasKeycloak={Boolean(keycloak)} />
                      )
                    }
                  />
                  <Route
                    exact
                    component={ForgotPassword}
                    path="/password/new"
                  />
                  <Route component={ResetPassword} path="/password/edit" />
                  {keycloak && useKeycloak && (
                    <Route component={VerifyAccount} path="/verify-account" />
                  )}

                  {/* ACCOUNT  */}
                  <ProtectedRoute
                    component={lazyLoadingComponents.Profile}
                    path="/settings"
                  />

                  {/* DEFAULT */}
                  <ProtectedRoute component={Projects} path="/new" />
                  <ProtectedRoute exact component={Projects} path="/" />
                  <ProjectWrapper>
                    <Switch>
                      {/* PROJECT SCENES */}
                      <ProtectedRoute
                        component={lazyLoadingComponents.NewFlow}
                        guards={[
                          {
                            getIsActive: () =>
                              getIsLoggedIn(store.getState()) === false,
                            pathname: '/sign_in',
                          },
                        ]}
                        path={`${PROJECT_PATH}/new-flow/canvases`}
                      />
                      <ProtectedRoute
                        component={lazyLoadingComponents.ApplicationSetup}
                        guards={[
                          {
                            getIsActive: () =>
                              getIsLoggedIn(store.getState()) === false,
                            pathname: '/sign_in',
                          },
                        ]}
                        path={`${PROJECT_PATH}/new-flow/application-setup`}
                      />
                      <ProtectedRoute
                        component={lazyLoadingComponents.ChatLogs}
                        path={`${PROJECT_PATH}/chat-logs/:dialog`}
                      />
                      <ProtectedRoute
                        component={lazyLoadingComponents.ProjectSettings}
                        path={`${PROJECT_PATH}/project-settings`}
                      />
                      <ProtectedRoute
                        component={lazyLoadingComponents.ResolveConflicts}
                        path={`${PROJECT_PATH}/resolve-conflicts`}
                      />
                      <Bounder>
                        <Switch>
                          <ProtectedRoute
                            component={Flow}
                            guards={[
                              {
                                getIsActive: () =>
                                  getIsLoggedIn(store.getState()) === false,
                                pathname: '/sign_in',
                              },
                            ]}
                            path={`${PROJECT_PATH}/flow`}
                          />
                          <ProtectedRoute
                            component={Rules}
                            path={`${PROJECT_PATH}/groups`}
                          />
                          <ProtectedRoute
                            component={Xpacks}
                            path={`${PROJECT_PATH}/xpacks`}
                          />
                          <ProtectedRoute
                            component={Intents}
                            path={`${PROJECT_PATH}/intents`}
                          />
                          <ProtectedRoute
                            component={Contexts}
                            path={`${PROJECT_PATH}/contexts`}
                          />
                          <ProtectedRoute
                            component={Entities}
                            path={`${PROJECT_PATH}/entities`}
                          />
                          <ProtectedRoute
                            component={Resources}
                            path={`${PROJECT_PATH}/resources`}
                          />
                          <ProtectedRoute
                            component={TestSetsEditor}
                            path={`${PROJECT_PATH}/test-sets`}
                          />
                          <ProtectedRoute
                            component={Dictionaries}
                            path={`${PROJECT_PATH}/dictionaries`}
                          />
                          <Redirect
                            from={PROJECT_PATH}
                            to={`${PROJECT_PATH}/entities`}
                          />
                        </Switch>
                      </Bounder>
                    </Switch>
                  </ProjectWrapper>
                  <Route component={NotFound} />
                </Switch>
              </React.Suspense>
            </ErrorBoundary>
          </ConnectedRouter>
        </Provider>
      </ThemeProvider>
    </KeycloakActionsProvider>
  );
};

export default Routes;
